import React from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { Navigate, Outlet, useLocation } from "react-router-dom";
import Loading from "../components/ui/Loading/Loading";
import { updateUserInfo } from "../app/slices/userSlice";
import { get } from "../services/api";
import {
  buildTenantRelativePath,
  getTenantPreviewSlugFromPath,
  getTenantPreviewSlugFromSearch,
  isCustomTenantHost,
} from "../utils/tenantPreview";

export default function NotLoggedIn({ user }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const tenantSlug =
    getTenantPreviewSlugFromPath(location.pathname) ||
    getTenantPreviewSlugFromSearch(location.search);
  // GitHub-style invite flow: a logged-out visitor who clicked "Log in to
  // accept" on /invite/:token arrives at /login?invite=<token>. Once they
  // authenticate, bounce them back to /invite/:token instead of the
  // generic home/account landing so the now-logged-in branch of the
  // InviteAccept page can auto-accept and join them.
  const inviteToken = `${
    new URLSearchParams(location.search).get("invite") || ""
  }`.trim();
  // General (reusable) invite link: a logged-out visitor who clicked "Log in
  // to join" on /join/:code arrives at /login?join=<code>. After auth, bounce
  // them back to /join/:code so the JoinByCode page can accept + join them.
  const joinCode = `${
    new URLSearchParams(location.search).get("join") || ""
  }`.trim();
  const nextPath = inviteToken
    ? buildTenantRelativePath(
        `/invite/${encodeURIComponent(inviteToken)}`,
        tenantSlug
      )
    : joinCode
      ? buildTenantRelativePath(
          `/join/${encodeURIComponent(joinCode)}`,
          tenantSlug
        )
      : tenantSlug
        ? buildTenantRelativePath("/home", tenantSlug)
        : isCustomTenantHost()
          ? "/home"
          : "/account";
  const authIdentity =
    `${user?.userinfo?._id || user?.userinfo?.guid || user?.userinfo?.username || user?.userinfo?.email || user?.userinfo?.mobile || "anon"}`.trim() ||
    "anon";

  const {
    data: restoredSession,
    isLoading: isRestoringSession,
  } = useQuery({
    queryKey: ["not-logged-in-bootstrap", location.pathname, authIdentity],
    queryFn: async () => {
      const response = await get("/api/v1/users/bootstrap", {
        skipAuthRedirect: true,
      });
      return response?.data || response || {};
    },
    enabled: !user?.userinfo,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (user?.userinfo || !restoredSession?.user) return;
    dispatch(updateUserInfo(restoredSession.user));
  }, [dispatch, restoredSession?.user, user?.userinfo]);

  if (!user?.userinfo && (isRestoringSession || restoredSession?.user)) {
    return <Loading />;
  }

  return user.userinfo ? (
    <Navigate to={nextPath} replace />
  ) : (
    <>
      <React.Suspense fallback={<Loading />}>
        <Outlet />
      </React.Suspense>
    </>
  );
}
